import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import DashboardSidebar from './components/layout/DashboardSidebar';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ArchivePage from './pages/ArchivePage';
import ArticlePage from './pages/ArticlePage';
import CMSPage from './pages/CMSPage';
import ContactPage from './pages/ContactPage';
import SubscriptionPage from './pages/SubscriptionPage';
import NotificationsPage from './pages/NotificationsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Dashboard pages
import DashboardHome from './pages/dashboard/DashboardHome';
import ManuscriptsList from './pages/dashboard/ManuscriptsList';
import AuthorDashboard from './pages/dashboard/AuthorDashboard';
import SubmitManuscript from './pages/dashboard/SubmitManuscript';
import ReviewerDashboard from './pages/dashboard/ReviewerDashboard';
import PublishedPage from './pages/dashboard/PublishedPage';
import UsersPage from './pages/dashboard/UsersPage';
import PagesPage from './pages/dashboard/PagesPage';
import AnalyticsPage from './pages/dashboard/AnalyticsPage';

// Public layout with navbar + footer
function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FA]">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Dashboard layout with sidebar
function DashboardLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <DashboardSidebar />
      <div className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </div>
    </div>
  );
}

// Route guard for roles
function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.some(r => user.roles?.includes(r))) return <Navigate to="/panel/mis-manuscritos" replace />;
  return children;
}

function AppContent() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<RegisterPage />} />
        <Route path="/archivo" element={<ArchivePage />} />
        <Route path="/articulo/:id" element={<ArticlePage />} />
        <Route path="/pagina/:slug" element={<CMSPage />} />
        <Route path="/contacto" element={<ContactPage />} />
        <Route path="/suscripcion" element={<SubscriptionPage />} />
        <Route path="/suscripcion/exito" element={<SubscriptionPage />} />
        <Route path="/recuperar-contrasena" element={<ForgotPasswordPage />} />
        <Route path="/restablecer-contrasena" element={<ResetPasswordPage />} />
      </Route>

      {/* Dashboard routes */}
      <Route element={<DashboardLayout />}>
        {/* Editorial (Admin/Editor) */}
        <Route path="/panel" element={
          <RequireRole roles={['Admin', 'Editor']}><DashboardHome /></RequireRole>
        } />
        <Route path="/panel/envios" element={
          <RequireRole roles={['Admin', 'Editor']}><ManuscriptsList /></RequireRole>
        } />
        <Route path="/panel/revision" element={
          <RequireRole roles={['Admin', 'Editor']}><ManuscriptsList filterStatus="En Revision" /></RequireRole>
        } />
        <Route path="/panel/dictaminados" element={
          <RequireRole roles={['Admin', 'Editor']}><ManuscriptsList filterStatus="dictaminados" /></RequireRole>
        } />
        <Route path="/panel/publicados" element={
          <RequireRole roles={['Admin', 'Editor']}><PublishedPage /></RequireRole>
        } />

        {/* Reviewer */}
        <Route path="/panel/mis-revisiones" element={
          <RequireRole roles={['Admin', 'Editor', 'Revisor']}><ReviewerDashboard /></RequireRole>
        } />

        {/* Author (any logged-in user) */}
        <Route path="/panel/mis-manuscritos" element={<AuthorDashboard />} />
        <Route path="/panel/enviar" element={<SubmitManuscript />} />

        {/* Admin only */}
        <Route path="/panel/usuarios" element={
          <RequireRole roles={['Admin']}><UsersPage /></RequireRole>
        } />
        <Route path="/panel/paginas" element={
          <RequireRole roles={['Admin']}><PagesPage /></RequireRole>
        } />
        <Route path="/panel/analiticas" element={
          <RequireRole roles={['Admin']}><AnalyticsPage /></RequireRole>
        } />

        {/* Notifications */}
        <Route path="/panel/notificaciones" element={<NotificationsPage />} />

        {/* Legacy redirect — old notifications pointed here */}
        <Route path="/panel/autor" element={<Navigate to="/panel/mis-manuscritos" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
